import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const labelCls = 'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55';

const StatTile = ({ label, value, testId }) => (
  <div
    className="bg-white border border-[#EFE6CF] rounded-2xl p-5"
    data-testid={testId}
  >
    <p className={`${labelCls} mb-2`}>{label}</p>
    <p className="font-display italic text-4xl text-[#0A1F2E] leading-none">{value}</p>
  </div>
);

const formatTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  } catch {
    return iso;
  }
};

const ErrorRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-white border border-[#EFE6CF] rounded-xl overflow-hidden"
      data-testid={`client-error-row-${row.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left px-4 py-3 hover:bg-[#FCF8EE] transition"
        data-testid={`client-error-toggle-${row.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#2CA8A0]/10 text-[#1A5C6E]">
              {row.scope || 'root'}
            </span>
            {row.user_email && (
              <span className="text-xs text-[#0A1F2E]/70">{row.user_email}</span>
            )}
            <span className="text-xs text-[#0A1F2E]/45 ml-auto">{formatTime(row.server_at)}</span>
          </div>
          <p className="text-sm text-[#0A1F2E] font-medium truncate">
            {row.message || '(no message)'}
          </p>
          {row.path && (
            <p className="text-xs text-[#0A1F2E]/55 font-mono mt-0.5 truncate">{row.path}</p>
          )}
        </div>
        <div className="shrink-0 pt-1">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[#EFE6CF] bg-[#FCF8EE]/50 space-y-3">
          {row.stack && (
            <div data-testid={`client-error-stack-${row.id}`}>
              <p className={`${labelCls} mb-1`}>Stack</p>
              <pre className="text-[11px] font-mono text-[#0A1F2E]/85 whitespace-pre-wrap break-words bg-white border border-[#EFE6CF] rounded p-2 max-h-64 overflow-auto">
                {row.stack}
              </pre>
            </div>
          )}
          {row.component_stack && (
            <div data-testid={`client-error-component-stack-${row.id}`}>
              <p className={`${labelCls} mb-1`}>Component stack</p>
              <pre className="text-[11px] font-mono text-[#0A1F2E]/85 whitespace-pre-wrap break-words bg-white border border-[#EFE6CF] rounded p-2 max-h-64 overflow-auto">
                {row.component_stack}
              </pre>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className={`${labelCls} mb-1`}>User agent</p>
              <p className="text-[#0A1F2E]/75 break-words">{row.user_agent || '-'}</p>
            </div>
            <div>
              <p className={`${labelCls} mb-1`}>IP</p>
              <p className="text-[#0A1F2E]/75 font-mono">{row.ip || '-'}</p>
            </div>
            <div>
              <p className={`${labelCls} mb-1`}>Client time</p>
              <p className="text-[#0A1F2E]/75">{formatTime(row.client_at)}</p>
            </div>
            <div>
              <p className={`${labelCls} mb-1`}>Server time</p>
              <p className="text-[#0A1F2E]/75">{formatTime(row.server_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminClientErrors = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('');
  const [limit, setLimit] = useState(100);
  const [clearing, setClearing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (scope) params.set('scope', scope);
      const res = await fetch(`${API}/api/admin/client-errors?${params.toString()}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not load client errors');
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [scope, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClear = useCallback(async () => {
    const target = scope ? `scope "${scope}"` : 'ALL client error rows';
    if (!window.confirm(`Delete ${target}? This cannot be undone.`)) return;
    setClearing(true);
    try {
      const params = new URLSearchParams();
      if (scope) params.set('scope', scope);
      const res = await fetch(
        `${API}/api/admin/client-errors${params.toString() ? `?${params.toString()}` : ''}`,
        { method: 'DELETE', credentials: 'include', headers: authHeaders() },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'Clear failed');
      toast.success(`Deleted ${body.deleted} row(s).`);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClearing(false);
    }
  }, [scope, fetchData]);

  const rows = data?.rows || [];
  const scopes = data?.scopes || [];
  const total = data?.total || 0;

  // Group rows by scope for the breakdown tiles
  const breakdown = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.scope || 'root';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#FCF8EE] py-10 px-4 sm:px-8" data-testid="admin-client-errors-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className={`${labelCls} mb-2`}>Admin · Diagnostics</p>
            <h1 className="font-display italic text-4xl sm:text-5xl text-[#0A1F2E] leading-tight">
              Client error log
            </h1>
            <p className="text-sm text-[#0A1F2E]/65 mt-2 max-w-2xl">
              Frontend crashes captured by the React ErrorBoundary via{' '}
              <code className="font-mono text-xs">navigator.sendBeacon</code> → POST{' '}
              <code className="font-mono text-xs">/api/client-errors</code>. Newest first.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#FCF8EE] transition disabled:opacity-50"
              data-testid="client-errors-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={clearing || loading || rows.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1F2E] text-white hover:bg-[#0A1F2E]/90 transition disabled:opacity-50"
              data-testid="client-errors-clear-btn"
            >
              <Trash2 size={14} />
              {clearing ? 'Clearing…' : scope ? `Clear "${scope}"` : 'Clear all'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="ce-scope" className={`${labelCls} mb-1.5 block`}>Scope</label>
            <select
              id="ce-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25"
              data-testid="client-errors-scope-select"
            >
              <option value="">All scopes</option>
              {scopes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ce-limit" className={`${labelCls} mb-1.5 block`}>Limit</label>
            <select
              id="ce-limit"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25"
              data-testid="client-errors-limit-select"
            >
              {[50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatTile label="Total in DB" value={total.toLocaleString()} testId="ce-stat-total" />
          <StatTile label="Showing" value={rows.length.toLocaleString('en-US', { timeZone: 'America/Chicago' })} testId="ce-stat-showing" />
          <StatTile label="Unique scopes" value={scopes.length} testId="ce-stat-scopes" />
          <StatTile
            label="Top scope"
            value={breakdown[0]?.[0] || '-'}
            testId="ce-stat-top-scope"
          />
        </div>

        {/* Body */}
        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 mb-6 flex items-center gap-2 text-sm"
            data-testid="client-errors-error"
          >
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="client-errors-loading">
            <Loader2 className="animate-spin text-[#2CA8A0]" size={32} />
          </div>
        ) : rows.length === 0 ? (
          <div
            className="bg-white border border-[#EFE6CF] rounded-2xl p-10 text-center"
            data-testid="client-errors-empty"
          >
            <p className="font-display italic text-2xl text-[#0A1F2E] mb-2">No crashes recorded.</p>
            <p className="text-sm text-[#0A1F2E]/60">
              Either the frontend is healthy or no errors have been reported yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="client-errors-list">
            {rows.map((r) => (
              <ErrorRow key={r.id} row={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminClientErrors;
